/**
 * Quick Entry Dialog — log a pipeline activity (new file, client invite,
 * portal completion, proposal sent, lodgement, approval, settlement, other).
 *
 * Opens from the Live Performance Dashboard toolbar. POSTs to
 * /api/gameplan/quick-entry and calls onSaved() so the parent can refresh.
 *
 * Users can either:
 *  - Tap a single-tap +1 tile (fast path, defaults count=1, no client), or
 *  - Expand a form to log a batched count, add a client name, or add a note.
 */
import React, { useMemo, useState } from 'react';
import {
  Activity, Award, ChevronDown, FileCheck, FilePlus, HelpCircle, Loader2,
  Send, Trophy, X, ClipboardCheck, Plus, Minus,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { backendUrl } from './gameplanUtils';

// Event catalogue — matches the backend ACTIVITY_EVENT_TYPES set.
export const QUICK_ENTRY_EVENTS = [
  { key: 'new_file',          label: 'New file',        short: 'File',       icon: FilePlus,       tone: 'bg-sky-500' },
  { key: 'client_invite',     label: 'Client invite',   short: 'Invite',     icon: Send,           tone: 'bg-violet-500' },
  { key: 'portal_completion', label: 'Portal complete', short: 'Complete',   icon: ClipboardCheck, tone: 'bg-amber-500' },
  { key: 'proposal_sent',     label: 'Proposal sent',   short: 'Proposal',   icon: FileCheck,      tone: 'bg-indigo-500' },
  { key: 'lodgement',         label: 'Lodgement',       short: 'Lodged',     icon: Activity,       tone: 'bg-blue-600' },
  { key: 'approval',          label: 'Approval',        short: 'Approved',   icon: Award,          tone: 'bg-emerald-500' },
  { key: 'settlement',        label: 'Settlement',      short: 'Settled',    icon: Trophy,         tone: 'bg-green-600' },
  { key: 'other',             label: 'Other',           short: 'Other',      icon: HelpCircle,     tone: 'bg-slate-500' },
];

const QuickEntryDialog = ({ open, onOpenChange, authToken, onSaved, todayCounts = {} }) => {
  const [expandedKey, setExpandedKey] = useState(null);
  const [count, setCount] = useState(1);
  const [clientName, setClientName] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(null); // holds key of the event currently saving

  const resetForm = () => {
    setExpandedKey(null);
    setCount(1);
    setClientName('');
    setNote('');
  };

  const submit = async (eventKey, opts = {}) => {
    if (saving) return;
    setSaving(eventKey);
    try {
      const body = {
        event_type: eventKey,
        count: opts.count ?? 1,
        client_name: (opts.client_name ?? '').trim() || undefined,
        note: (opts.note ?? '').trim() || undefined,
      };
      const res = await fetch(`${backendUrl}/api/gameplan/quick-entry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const label = QUICK_ENTRY_EVENTS.find((e) => e.key === eventKey)?.label ?? eventKey;
      toast.success(
        `${body.count > 1 ? `+${body.count}` : '+1'} ${label}${body.client_name ? ` · ${body.client_name}` : ''}`,
      );
      onSaved?.();
      if (opts.close !== false) {
        resetForm();
      }
    } catch (e) {
      toast.error('Could not save — please try again');
      console.error(e);
    } finally {
      setSaving(null);
    }
  };

  const handleClose = (o) => {
    if (!o) resetForm();
    onOpenChange(o);
  };

  const canSubmit = useMemo(() => Boolean(expandedKey) && count >= 1, [expandedKey, count]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="quick-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
            <Plus className="w-5 h-5 text-emerald-600" />
            Quick Entry
          </DialogTitle>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Log a pipeline activity in one tap. Feeds the Activity strip on your Live dashboard.
          </p>
        </DialogHeader>

        {/* One-tap grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
          {QUICK_ENTRY_EVENTS.map((ev) => {
            const isSaving = saving === ev.key;
            const todayCount = todayCounts[ev.key] || 0;
            const Icon = ev.icon;
            return (
              <button
                key={ev.key}
                type="button"
                disabled={saving && !isSaving}
                onClick={() => submit(ev.key, { close: false })}
                data-testid={`quick-entry-tile-${ev.key}`}
                className={cn(
                  'relative flex flex-col items-center gap-1 p-3 rounded-xl border border-slate-200 dark:border-slate-700',
                  'hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/40',
                  'transition-all group active:scale-95',
                  isSaving && 'opacity-70',
                )}
              >
                <span className={cn('w-8 h-8 rounded-full flex items-center justify-center text-white shadow-sm', ev.tone)}>
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                </span>
                <span className="text-[11.5px] font-semibold text-slate-800 dark:text-slate-100 leading-tight text-center">
                  {ev.short}
                </span>
                <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">+1</span>
                {todayCount > 0 && (
                  <span className="absolute top-1 right-1 text-[10px] font-bold bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-1.5 py-0.5 rounded-full">
                    {todayCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Divider + expand */}
        <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3">
          <button
            type="button"
            className="text-xs font-semibold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 flex items-center gap-1.5"
            onClick={() => setExpandedKey((k) => (k ? null : QUICK_ENTRY_EVENTS[0].key))}
            data-testid="quick-entry-expand-toggle"
          >
            <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', expandedKey && 'rotate-180')} />
            Add with client name, batch or notes
          </button>

          {expandedKey && (
            <div className="mt-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-3">
              {/* Event picker */}
              <div>
                <Label className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block">Event</Label>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_ENTRY_EVENTS.map((ev) => (
                    <button
                      key={ev.key}
                      type="button"
                      onClick={() => setExpandedKey(ev.key)}
                      data-testid={`quick-entry-picker-${ev.key}`}
                      className={cn(
                        'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border transition-colors',
                        expandedKey === ev.key
                          ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100'
                          : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400',
                      )}
                    >
                      {ev.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Count + client name */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block">Count</Label>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-8 w-8 shrink-0"
                            onClick={() => setCount((c) => Math.max(1, c - 1))} data-testid="quick-entry-count-dec">
                      <Minus className="w-3.5 h-3.5" />
                    </Button>
                    <Input type="number" min={1} max={99} value={count}
                           onChange={(e) => setCount(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                           className="h-8 text-center tabular-nums" data-testid="quick-entry-count" />
                    <Button variant="outline" size="icon" className="h-8 w-8 shrink-0"
                            onClick={() => setCount((c) => Math.min(99, c + 1))} data-testid="quick-entry-count-inc">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block">Client name (optional)</Label>
                  <Input value={clientName} onChange={(e) => setClientName(e.target.value)}
                         placeholder="e.g. Jane Doe" className="h-8"
                         data-testid="quick-entry-client" />
                </div>
              </div>

              {/* Note */}
              <div>
                <Label className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 block">Note (optional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="Short context — visible to you only"
                          className="text-sm resize-none" data-testid="quick-entry-note" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-2 gap-2">
          <Button variant="ghost" onClick={() => handleClose(false)} data-testid="quick-entry-cancel">
            <X className="w-4 h-4 mr-1.5" /> Close
          </Button>
          {expandedKey && (
            <Button
              onClick={() => submit(expandedKey, {
                count, client_name: clientName, note, close: true,
              })}
              disabled={!canSubmit || Boolean(saving)}
              data-testid="quick-entry-save"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Log entry
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickEntryDialog;
export { QuickEntryDialog };
